import { useRouter } from 'next/router'
import { useSnapshot } from 'valtio'
import { type ReactElement, useEffect } from 'react'
import Head from 'next/head'
import cn from 'classnames'
import format from 'date-fns/format'
import { executeTransactionRetrieve } from '../../../../../shared/apiUtils/executeTransactionRetrieve'
import {
  determineBrowserTabName, DeterminePrimaryColorIconPoweredBy
} from '../../../../../shared/themes/logoFunctions'
import { proxyGlobalAuth } from '../../../../../shared/proxies/proxyGlobalAuth'
import { transactionProxy } from '../../../../../shared/proxies/transactionProxy'
import { usdFormatter } from '../../../../../shared/utils/formattingUtils'

export function Container (): ReactElement | null {
  const router = useRouter()
  const { siteTheme: { buttonColors }, siteDomainEnum } = useSnapshot(proxyGlobalAuth)
  const { transaction } = useSnapshot(transactionProxy)
  const { id } = router.query

  useEffect(() => {
    if (!id) { return }
    void executeTransactionRetrieve(id as string)
  }, [id])

  useEffect(() => {
    // Wait for 2 seconds before opening the print dialog
    const timeoutId = setTimeout(() => {
      window.print()
    }, 2000)

    // Clean up the timeout if the component unmounts before it finishes
    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  return (
    <>
      <Head>
        <title>{`Print Receipt for Transaction ${id as string} - ${determineBrowserTabName()}`}</title>
      </Head>

      <div className={cn('flex w-full print:hidden')}>
        <div className="w-1/2 text-center p-4">
          <button
            onClick={() => { window.print() }}
            className={cn(buttonColors.classes, 'w-full p-4 rounded text-white')}
          >
            Print
          </button>
        </div>
        <div className="w-1/2 text-center p-4">
          <button
            onClick={() => { window.close() }}
            className={cn(buttonColors.classes, 'w-full p-4 rounded text-white')}
          >
            Close Tab
          </button>
        </div>
      </div>

      <div className="flex flex-row justify-between  p-12 border-b border-gray-300 text-l">
        <div>
          <h2>Billing Address</h2>
          <p>{transaction?.firstName} {transaction?.lastName}</p>
          <p>{transaction?.addressOne}</p>
          <p>{transaction?.addressTwo}</p>
          <p> {transaction?.city} {transaction?.state}, {transaction?.postalCode}</p>
        </div>
        <div className="w-1/4">
          <DeterminePrimaryColorIconPoweredBy theme={siteDomainEnum} />
        </div>
      </div>

      <div className="p-12 text-base">
        <h1>Hi,</h1>
        <p>
          This is a receipt for your transaction with invoice #: {transaction?.invoiceNumber} placed on  {transaction ? format(Date.parse(transaction.transactedAt), 'MMMM d, yyyy') : null}. No payment is due.
        </p>

        <div className="flex flex-row justify-between items-end pt-8 border-b border-gray-300">
          <div>
            <div className="text-base">Paid By: {transaction?.customerCardId ? 'Credit Card' : 'Electronic Check'}</div>
            <div className="text-base">Transaction ID: {transaction?.ppSslTxnId}</div>
            <div className="mt-6">Total</div>
          </div>
          <div>
            <div>{transaction?.amountCents ? usdFormatter.format(transaction.amountCents) : null}</div>
          </div>
        </div>
      </div>

    </>
  )
}
